import { Appointment, Encounter, Location } from 'fhir/r4b';
import { EvolveUser } from 'src/hooks/useEvolveUser';
import {
  allLicensesForPractitioner,
  checkEncounterHasPractitioner,
  EncounterVisitType,
  getEncounterVisitType,
  getTelemedVisitStatus,
  isAppointmentLocked,
  PractitionerLicense,
  StateType,
  TelemedAppointmentStatusEnum,
} from 'utils';

export type GetAppointmentAccessibilityDataProps = {
  locationVirtual?: Location;
  encounter: Encounter;
  appointment?: Appointment;
  user?: EvolveUser;
};

export type GetAppointmentAccessibilityDataResult = {
  allLicenses?: PractitionerLicense[];
  licensedPractitionerStates?: string[];
  state?: StateType;
  isPractitionerLicensedInState: boolean;
  status?: TelemedAppointmentStatusEnum;
  isEncounterAssignedToCurrentPractitioner: boolean;
  isStatusEditable: boolean;
  isAppointmentReadOnly: boolean;
  isCurrentUserHasAccessToAppointment: boolean;
  isAppointmentLocked: boolean;
  visitType: EncounterVisitType;
};

export const getAppointmentAccessibilityData = ({
  locationVirtual,
  encounter,
  appointment,
  user,
}: GetAppointmentAccessibilityDataProps): GetAppointmentAccessibilityDataResult => {
  const allLicenses = user?.profileResource && allLicensesForPractitioner(user.profileResource);
  const licensedPractitionerStates = allLicenses?.map((item) => item.state);
  const state = locationVirtual?.address?.state as StateType;

  const isPractitionerLicensedInState =
    !!state && !!licensedPractitionerStates && licensedPractitionerStates.includes(state as StateType);

  const status = encounter ? getTelemedVisitStatus(encounter.status, appointment?.status) : undefined;

  const isEncounterAssignedToCurrentPractitioner =
    !!user?.profileResource && !!encounter && checkEncounterHasPractitioner(encounter, user.profileResource);

  const isStatusEditable =
    !!status && ![TelemedAppointmentStatusEnum.complete, TelemedAppointmentStatusEnum.ready].includes(status);

  const isCurrentUserHasAccessToAppointment =
    isPractitionerLicensedInState &&
    (status === TelemedAppointmentStatusEnum.ready || isEncounterAssignedToCurrentPractitioner);

  // Check if appointment is locked via meta tag
  const isAppointmentLockedByMetaTag = appointment ? isAppointmentLocked(appointment) : false;
  const visitType = getEncounterVisitType(encounter);
  const isFollowup = visitType === 'follow-up';

  const isAppointmentReadOnly = (() => {
    return isAppointmentLockedByMetaTag && !isFollowup;
  })();

  return {
    allLicenses,
    licensedPractitionerStates,
    state,
    isPractitionerLicensedInState,
    status,
    isEncounterAssignedToCurrentPractitioner,
    isStatusEditable,
    isAppointmentReadOnly,
    isCurrentUserHasAccessToAppointment,
    isAppointmentLocked: isAppointmentLockedByMetaTag,
    visitType,
  };
};
